import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { getFormValues, getFormSyncErrors } from "redux-form";
import SmartPortal from "../SmartPortal";
import DynamicForm from "../DynamicForm";
import Loader from "../Loader";
import ReactSVG from "react-svg";
import BaseService from "../../../../services/BaseService";
import cx from "classnames";
import { isEmpty } from "lodash";

import { GET_MAX_FILE_SIZE_FROM_DICT } from "constants/index";

import { ModalWindow } from "ui-core-dashboard";
import MessageModal from "../MessageModal";

import "./styles.scss";

const mapStateToProps = (state, ownProps) => {
    return {
        // form: ownProps.formName, // set form name from ownProps
        formValues: getFormValues(ownProps.formName)(state),
        formErrors: getFormSyncErrors(ownProps.formName)(state),
    };
};

const mapDispatchToProps = {};

const DynamicFormPopup = props => {
    const {
        t,
        title = "",
        portalPlacement = "right",
        targetElement,
        formName,
        staticDynData = null,
        requestParams,
        repositionTrigger,
        delayedRepositionTrigger,
        delayedRepositionDelay = 500,
        onClose,
        onArrowUp,
        onArrowDown,
        arrowIndex,
        minArrowIndex,
        maxArrowIndex,
        activeRowNum,
        maxRowNum,
        formValues,
        formErrors,
        onSave,
        onDelete,
        onDataChange,
        getConvertedWidgetValue,
        widgetsQuantity,
    } = props;

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        itemId: null,
        itemName: "",
        message: t("deleteConfirmMessage"),
        primaryButton: t("remove"),
        secondaryButton: t("cancel"),
    });

    const [dynData, setDynData] = useState(staticDynData);
    const [isLoading, setIsLoading] = useState(false);

    const confirmModalOpenRef = useRef(false);

    const maxFileSize = useMemo(() => GET_MAX_FILE_SIZE_FROM_DICT("form"), []);

    function closeConfirmModal(e) {
        confirmModalOpenRef.current = false;
        setConfirmModal(prev => ({
            ...prev,
            open: false,
        }));
    }

    function openConfirmModal() {
        if (isLoading) {
            return;
        }
        confirmModalOpenRef.current = true;
        setConfirmModal(prev => ({
            ...prev,
            open: true,
        }));
    }

    const handleClose = () => {
        if (!confirmModalOpenRef.current && onClose && typeof onClose === "function") {
            onClose();
        }
    };

    const handleSave = () => {
        if (!isLoading && isEmpty(formErrors) && onSave && typeof onSave === "function") {
            try {
                const widgets = dynData.flatMap(block => block.widgets);
                const newFormValues = {};
                Object.keys(formValues).forEach(key => {
                    if (!key.includes("cached_ajax_options")) {
                        let newValue = formValues[key];
                        if (newValue && typeof newValue === 'object') {
                            newValue = newValue.value || newValue.key;
                        }
                        if (activeRowNum === 0) {
                            newFormValues[`${key}@*${maxRowNum + 1}`] = newValue; // add a new row
                        } else {
                            newFormValues[key] = newValue; // adjust existing row
                        }
                    }
                });
                const type = activeRowNum === 0 ? "add" : "edit";
                // const values = type === "add" ? newFormValues : formValues;
                const values = newFormValues;
                const responseCallback = success => {
                    setIsLoading(false);
                    if (success) {
                        if (onDataChange && typeof onDataChange === "function") {
                            const index = type === "add" ? maxRowNum + 1 : activeRowNum;
                            // properly convert select and date values (in readable format)
                            const convertedFormValues = {};
                            widgets.forEach(widget => {
                                // Convert timestamp to date if widgetType is "date"
                                const value = getConvertedWidgetValue(widget, formValues);
                                convertedFormValues[widget.key] = value;
                            });
                            onDataChange(type, index, convertedFormValues);
                        }
                        handleClose();
                    }
                };
                setIsLoading(true);
                onSave(formName, responseCallback, false, values);
            } catch (e) {
                setIsLoading(false);
                console.error("DynamicFormPopup::handleSave: Error ", e);
            }
        }
    };

    const handleDelete = () => {
        if (onDelete && typeof onDelete === "function") {
            closeConfirmModal();
            try {
                const values = Object.keys(formValues);
                const responseCallback = success => {
                    setIsLoading(false);
                    if (success) {
                        if (onDataChange && typeof onDataChange === "function") {
                            onDataChange("delete", activeRowNum, values);
                        }
                        handleClose();
                    }
                };
                setIsLoading(true);
                onDelete(formName, responseCallback, false, values);
            } catch (e) {
                setIsLoading(false);
                console.error("DynamicFormPopup::handleSave: Error ", e);
            }
        }
    };

    const handleUp = () => {
        // console.log("** up **");
        if (onArrowUp && typeof onArrowUp === "function") {
            onArrowUp();
        }
    };

    const handleDown = () => {
        // console.log("** down **");
        if (onArrowDown && typeof onArrowDown === "function") {
            onArrowDown();
        }
    };

    const getDynParams = async params => {
        setIsLoading(true);
        try {
            const response = await BaseService.get("dinamic_params", { data: params });
            setIsLoading(false);
            // remove title from the blocks and set the view to form in case of the grid_mod
            if (response.success && response.result && response.result.blocks) {
                const blocks = response.result.blocks.map(block => {
                    if (block.view === "grid_mod") {
                        return {
                            ...block,
                            title: "*",
                            view: "form",
                            // adjust grid_mod widgets with hasExValue in order to get both value and label of ajax selected options
                            // to show readable value to the user once some city or dict data is saved
                            // f.e: Київ instead of 800000
                            widgets: block.widgets.map(widget => ({ ...widget, hasExValue: true })),
                        };
                    }
                    return { ...block, title: "*" };
                });
                setDynData(blocks);
            }
        } catch (e) {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (requestParams) {
            getDynParams(requestParams);
        }
    }, []);

    return (
        <SmartPortal
            portalClassName={cx("portal-dynamic-form-popup", {
                [`widgets-quantity-${widgetsQuantity}`]: !!widgetsQuantity,
            })}
            targetElement={targetElement}
            portalPlacement={portalPlacement}
            portalKey={formName}
            repositionTrigger={repositionTrigger}
            delayedRepositionTrigger={delayedRepositionTrigger}
            delayedRepositionDelay={delayedRepositionDelay}
            sizeTrigger={dynData}
            onClickOutsidePreventFieldKeys={formValues && Object.keys(formValues)}
            onClickOutside={handleClose}
        >
            <div className="portal-dynamic-form__header">
                <div className="portal-dynamic-form__header-title">
                    {t(title)} {activeRowNum === 0 ? "" : arrowIndex}
                </div>
                <div className="portal-dynamic-form__header-actions">
                    {activeRowNum !== 0 && (
                        <>
                            <div
                                className={cx("button-hover-half-circle", { disabled: arrowIndex === minArrowIndex })}
                                onClick={handleUp}
                            >
                                <ReactSVG className="button-icon" src={`/data/svg/chevron_up.svg`} />
                            </div>
                            <div
                                className={cx("button-hover-half-circle", { disabled: arrowIndex === maxArrowIndex })}
                                onClick={handleDown}
                            >
                                <ReactSVG className="button-icon" src={`/data/svg/chevron_down.svg`} />
                            </div>
                        </>
                    )}
                    <div className="button-hover-half-circle" onClick={handleClose}>
                        <ReactSVG className="button-icon" src={`/data/svg/input_clear.svg`} />
                    </div>
                </div>
            </div>
            <div className="portal-dynamic-form__form ordering-component-ui-core-wrapper">
                {isLoading && <Loader />}
                {dynData && (
                    <DynamicForm
                        t={t}
                        formName={formName}
                        dynData={dynData}
                        objectId={requestParams && requestParams.objectId}
                        currentId={requestParams && requestParams.objectId}
                        objectType={requestParams && requestParams.objectType}
                        maxFileSize={maxFileSize}
                        liveSave={false}
                        portalPlacement={portalPlacement}
                        customSelectPortalClassName="dynamic-form-popup-select-portal-menu"
                        isSelectPortalMenuFlexible
                    />
                )}
            </div>
            <div className="portal-dynamic-form__footer">
                {activeRowNum !== 0 && (
                    <button
                        title={t("reference.delete")}
                        className={cx("portal-dynamic-form__footer-button portal-dynamic-form__footer-button--delete", {
                            disabled: isLoading,
                        })}
                        type="button"
                        onClick={() => openConfirmModal()}
                    >
                        <ReactSVG className="footer-button__icon-bin" src={`/data/svg/icon-bin.svg`} />
                        <span>{t("remove")}</span>
                    </button>
                )}
                <button
                    title={t("save")}
                    className={cx("portal-dynamic-form__footer-button portal-dynamic-form__footer-button--save", {
                        disabled: isLoading || !dynData || !isEmpty(formErrors),
                    })}
                    type="button"
                    onClick={handleSave}
                >
                    <ReactSVG src={`/data/svg/input_save.svg`} />
                    <span>{t("save")}</span>
                </button>
            </div>
            {confirmModalOpenRef.current && (
                <ModalWindow
                    onClose={closeConfirmModal}
                    className="modal-small ordering-component-ui-core-wrapper ignore-clickoutside"
                >
                    <MessageModal
                        titleModal={t("deleteConfirm")}
                        contentModalText={t(confirmModal.message)}
                        dangerButton
                        dangerButtonText={t(confirmModal.primaryButton)}
                        onClickDangerButton={handleDelete}
                        secondaryButton
                        secondaryButtonText={t(confirmModal.secondaryButton)}
                        onClickSecondaryButton={closeConfirmModal}
                    />
                </ModalWindow>
            )}
        </SmartPortal>
    );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(DynamicFormPopup));
